import { NavLink, Outlet, useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, ListTree, ClipboardList, CalendarRange, GitBranch, AlertTriangle,
  HardHat, Wallet, FlaskConical, Package, Users, FileDown, LogOut, Menu, X, Wrench,
  Radar, ScanText, CloudOff, RefreshCw, Wifi,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { api } from "@/lib/api";
import { cacheGet, cacheSet } from "@/lib/offline";
import { Badge2 } from "@/components/Bits";
import { Logo } from "@/components/Logo";
import { TrialBanner } from "@/components/TrialGuard";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "dashboard", label: "Command Center", icon: LayoutDashboard },
  { to: "risk", label: "Risk Radar (AI)", icon: Radar },
  { to: "wbs", label: "WBS & Quantities", icon: ListTree },
  { to: "boq", label: "AI BOQ Parser", icon: ScanText },
  { to: "dpr", label: "Daily Progress (DPR)", icon: ClipboardList },
  { to: "planning", label: "Tiered Planning", icon: CalendarRange },
  { to: "cpm", label: "Critical Path", icon: GitBranch },
  { to: "hindrances", label: "Hindrance Matrix", icon: AlertTriangle },
  { to: "resources", label: "Resources & Manpower", icon: HardHat },
  { to: "costing", label: "Costing & EVM", icon: Wallet },
  { to: "whatif", label: "What-If Modeler", icon: FlaskConical },
  { to: "store", label: "Store & Procurement", icon: Package },
  { to: "kpi", label: "Employee KPIs", icon: Users },
  { to: "reports", label: "Export Center", icon: FileDown },
];

export default function Layout() {
  const { pid } = useParams();
  const { user, logout } = useAuth();
  const { online, pending, syncing, sync } = useOffline();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!pid) return;
    api.get(`/projects/${pid}`)
      .then(({ data }) => { setProject(data); cacheSet(`project:${pid}`, data); })
      .catch(async (e) => {
        const cached = await cacheGet(`project:${pid}`);
        if (cached) setProject(cached);
        else if (e.response) navigate("/projects");
      });
  }, [pid, navigate]);

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className={cn(
          "fixed lg:static z-40 inset-y-0 left-0 w-64 border-r border-border bg-card flex flex-col transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        <div className="h-14 flex items-center gap-2 px-3 border-b border-border">
          <Logo size="sm" />
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="close-sidebar-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <button
          onClick={() => navigate("/projects")}
          className="text-left px-3 py-2.5 border-b border-border hover:bg-muted transition-colors duration-150"
          data-testid="project-switcher-btn"
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Active Project</div>
          <div className="text-[13px] font-medium truncate">{project?.name || "Select project"}</div>
          <div className="text-[11px] text-muted-foreground truncate">{project?.location}</div>
        </button>

        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={`/p/${pid}/${n.to}`}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.to}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2.5 px-3 py-2 text-[13px] border-l-2 transition-colors duration-150",
                  isActive
                    ? "border-primary bg-primary/10 text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
                )
              }
            >
              <n.icon className="w-4 h-4 shrink-0" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          {!online || pending ? (
            <button
              onClick={() => online && sync()}
              className={cn(
                "w-full flex items-center gap-2 mb-2.5 px-2 py-1.5 text-[11px] border transition-colors duration-150",
                online
                  ? "border-[#F97316]/40 bg-[#F97316]/10 text-[#F97316] hover:bg-[#F97316]/20"
                  : "border-red-500/40 bg-red-500/10 text-red-500"
              )}
              data-testid="offline-status-pill"
            >
              {online ? (
                <RefreshCw className={cn("w-3.5 h-3.5 shrink-0", syncing && "animate-spin")} />
              ) : (
                <CloudOff className="w-3.5 h-3.5 shrink-0" />
              )}
              <span className="truncate">
                {online ? `Sync ${pending} pending ${pending === 1 ? "entry" : "entries"}` : "Offline — entries saved locally"}
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-1.5 mb-2.5 text-[10px] uppercase tracking-wider text-emerald-500" data-testid="online-status-pill">
              <Wifi className="w-3 h-3" /> Synced
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium truncate">{user?.name}</div>
              <Badge2 tone="primary" testId="user-role-badge">{user?.role}</Badge2>
            </div>
            <ThemeToggle showLabel={false} />
            <button onClick={logout} className="p-1.5 hover:bg-muted rounded transition-colors duration-150" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-1 -ml-0.5">
            <Link to="/om/dashboard" className="inline-flex items-center gap-1.5 text-[11.5px] font-medium hover:underline" style={{ color: "#C9A84C" }} data-testid="sidebar-om-link">
              <Wrench className="w-3.5 h-3.5" /> Operations &amp; Maintenance
            </Link>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2.5 leading-relaxed" data-testid="sidebar-tagline">
            <Link to="/settings" className="text-[#F97316] hover:underline" data-testid="sidebar-settings-link">Account security</Link>
            <span className="block mt-1">
              <Link to="/pricing" className="text-[#F97316] hover:underline" data-testid="sidebar-billing-link">Plans &amp; billing</Link>
            </span>
            <span className="block mt-1">
              <Link to="/faq" className="hover:text-[#F97316]" data-testid="sidebar-faq-link">FAQ</Link> ·{" "}
              <Link to="/privacy-policy" className="hover:text-[#F97316]" data-testid="sidebar-privacy-link">Privacy</Link> ·{" "}
              <Link to="/terms-of-service" className="hover:text-[#F97316]" data-testid="sidebar-terms-link">Terms</Link>
            </span>
            {user?.role === "admin" || user?.role === "superadmin" ? (
              <Link to="/enquiries" className="block mt-1 text-[#F97316] hover:underline" data-testid="sidebar-enquiries-link">
                Enquiries &amp; demos
              </Link>
            ) : null}
            {user?.role === "superadmin" ? (
              <Link to="/admin/data-editor" className="block mt-1 text-[#F97316] hover:underline" data-testid="sidebar-data-editor-link">
                Data editor
              </Link>
            ) : null}
            <span className="block mt-1">Plan Smarter. Build Stronger. Deliver on Time.</span>
            <span className="block text-[9px] uppercase tracking-wider mt-1 text-[#F97316]">A Sarthi Associates product</span>
          </p>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <TrialBanner />
        <div className="lg:hidden h-12 flex items-center gap-2 px-3 border-b border-border sticky top-0 z-30 bg-background/85 backdrop-blur-md">
          <button onClick={() => setOpen(true)} data-testid="open-sidebar-btn">
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold truncate">{project?.name || "Plan-Sarthi"}</span>
          {!online ? (
            <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-500" data-testid="mobile-offline-badge">
              <CloudOff className="w-3.5 h-3.5" /> Offline
            </span>
          ) : pending ? (
            <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#F97316]" data-testid="mobile-pending-badge">
              <RefreshCw className={cn("w-3.5 h-3.5", syncing && "animate-spin")} /> {pending} queued
            </span>
          ) : null}
        </div>
        <div className="p-3 sm:p-5 max-w-[1800px] w-full min-w-0 overflow-x-hidden">
          <Outlet context={{ project }} />
        </div>
      </main>
      {open ? <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} /> : null}
    </div>
  );
}
